import React, { useState, useEffect, Fragment } from 'react'

import { bool, number, string } from 'prop-types'

import Pagination from 'Pods/Profile/Products/Listing/Pagination'
import Loader from 'Pods/Shared/Loader'
import SharedTable from 'Pods/Shared/Table/SharedTable'

import ProductInfo from '../ProductInfo'
import ProductPrice from '../ProductPrice'
import ProductRate from '../ProductRate'
import QueryField from '../QueryField'
import EmptyState from './EmptyState'
import SubscriptionActions from './SubscriptionActions'
import useSubscriptionPurchases from '../../Hooks/UseSubscriptionPurchases'
import useSubscriptionTracking from '../../Hooks/useSubscriptionTracking'
import {
  defaultSubscriptionFilters, defaultSubscriptionTable,
} from '../../Static/constants'

function SubscriptionTab({
  isSubscriber = false, modelId = null, rating = null, subscriptionDownloadsPerMonth = 25,
  editWithAiEnabled = false,
}) {
  const [filters, setFilters] = useState(defaultSubscriptionFilters)
  const { trackTabView, trackDownloadClick } = useSubscriptionTracking()

  const { data, isLoading } = useSubscriptionPurchases({
    query: filters.query,
    page: filters.page,
  })

  useEffect(() => {
    trackTabView()
  }, [])


  function handleSearch(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }))
  }

  function handlePageChange({ page }) {
    setFilters((prev) => ({ ...prev, page }))
  }

  function handleDownloadClick(itemId, isRedownload) {
    trackDownloadClick(itemId, isRedownload)
  }

  function renderPlaceholder() {
    return (
      <div className="u-text-center no-purchases-message">
        <span>No subscription downloads match your search.</span>
      </div>
    )
  }

  function makeTable(items) {
    const rows = items.map(({ id, attributes }) => ({
      id,
      columns: [{
        content: <ProductInfo storeCreditsUsed={0} {...attributes} />,
      }, {
        content: <ProductPrice {...attributes} />,
      }, {
        content: <ProductRate {...attributes} modelId={modelId} rating={rating} />,
      }, {
        content: (
          <SubscriptionActions
            {...attributes}
            rowId={id}
            editWithAiEnabled={editWithAiEnabled}
            onDownloadClick={handleDownloadClick}
          />
        ),
      }],
    }))

    return { ...defaultSubscriptionTable, rows }
  }

  if (!data || isLoading) {
    return <Loader />
  }

  const { data: tableData, meta } = data

  if (!filters.query && tableData.length === 0) {
    if (!isSubscriber) {
      return (
        <EmptyState
          title="No subscription downloads yet"
          description={
            `Get access to up to ${subscriptionDownloadsPerMonth} models per month`
            + ' with a subscription and download premium assets anytime.'
          }
          linkText="Explore Subscription"
          linkHref="/subscription"
        />
      )
    }

    return (
      <EmptyState
        title="Your subscription is ready"
        description="Pick your first model and it will show up here instantly."
        linkText="View Subscription models"
        linkHref="/3d-models?page=1&subscribed=1"
      />
    )
  }

  return (
    <Fragment>
      <div className="cgt-mb-10 row">
        <div className="col-12 col-md-9">
          <QueryField
            query={filters.query}
            search={handleSearch}
            isDisabled={isLoading}
          />
        </div>
      </div>
      <div className="table-responsive table-responsive--default legacy-responsive-table">
        <SharedTable table={makeTable(tableData)} renderPlaceholder={renderPlaceholder} />
      </div>
      <div className="cgt-mb-10">
        <Pagination
          hidePerPage
          filters={{
            ...filters,
            page: Number(filters.page),
          }}
          onFiltersChange={handlePageChange}
          meta={meta}
        />
      </div>
    </Fragment>
  )
}

SubscriptionTab.propTypes = {
  isSubscriber: bool,
  modelId: string,
  rating: string,
  subscriptionDownloadsPerMonth: number,
  editWithAiEnabled: bool,
}

export default SubscriptionTab
